import Section from "@/components/settings/Section";
import StudyGoalSelector from "@/components/shared/StudyGoalSelector";
import { useAPI } from "@/contexts/APIContext";
import { useSettings } from "@/contexts/SettingsContext";
import handleError from "@/utils/handleError";
import { useEffect, useState } from "react";
import { Const } from "snudbud-backbone-sdk";

interface GoalSectionProps {
  initialGoal: Const.DailyGoal;
}

export default function GoalSection({ initialGoal }: GoalSectionProps) {
  const { api, updateUser } = useAPI();
  const { setSuccessMessage, setErrorMessage } = useSettings();

  const [dailyGoal, setDailyGoal] = useState<Const.DailyGoal>("medium");
  const [originalDailyGoal, setOriginalDailyGoal] = useState<Const.DailyGoal>("medium");
  const [isSaving, setIsSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  // Initialize goal data
  useEffect(() => {
    if (!initialGoal) return;

    setDailyGoal(initialGoal);
    setOriginalDailyGoal(initialGoal);
  }, [initialGoal]);

  // Check for changes
  useEffect(() => {
    const changed = dailyGoal !== originalDailyGoal;
    setIsChanged(changed);
  }, [dailyGoal, originalDailyGoal]);

  const handleSave = async () => {
    setIsSaving(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      // Update daily goal
      await api.user.update({ dailyGoal });

      setOriginalDailyGoal(dailyGoal);
      await updateUser(true);
      setSuccessMessage("Daily learning goal updated successfully");
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to update daily learning goal");
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGoalChange = (goal: Const.DailyGoal) => {
    setDailyGoal(goal);
  };

  return (
    <Section title="Daily Learning Goal" onSave={handleSave} isChanged={isChanged} isSaving={isSaving} id="goal">
      <StudyGoalSelector selectedGoal={dailyGoal} onChange={handleGoalChange} />
    </Section>
  );
}
